import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Helmet } from 'react-helmet';
import {
  MusicNotes, ChartLineUp, CurrencyDollar, Megaphone, Star, UsersThree,
  ForkKnife, CookingPot, Camera, VideoCamera, Certificate, Buildings,
  MagicWand, PaintBrush, Martini, ArrowRight, Check, CaretDown, CaretUp, X,
} from '@phosphor-icons/react';
import VENDOR_CONFIGS, { ALL_VENDOR_CATEGORIES } from '../utils/vendorPartnerData';

const ICON_MAP = {
  MusicNotes, ChartLineUp, CurrencyDollar, Megaphone, Star, UsersThree,
  ForkKnife, CookingPot, Camera, VideoCamera, Certificate, Buildings,
  MagicWand, PaintBrush, Martini,
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      variants={fadeUp}
      custom={idx}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="border-b border-[#E5E5E0]"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-6 text-left group"
        data-testid={`faq-item-${idx}`}
      >
        <span className="text-lg font-semibold text-[#0A0A0A] pr-8 group-hover:text-[#FF4400] transition-colors">{q}</span>
        {open ? <CaretUp size={20} weight="bold" /> : <CaretDown size={20} weight="bold" />}
      </button>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="pb-6"
        >
          <p className="text-[#525252] text-base leading-relaxed">{a}</p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function VendorPartnerPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const config = VENDOR_CONFIGS[category];
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -60]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);

  if (!config) {
    return (
      <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black text-[#0A0A0A] mb-4">Category Not Found</h1>
          <p className="text-[#525252] mb-8">This vendor category doesn't exist yet.</p>
          <Link to="/partners" className="bg-[#FF4400] text-white px-8 py-4 font-bold hover:bg-[#E63D00] transition-colors">
            View All Categories
          </Link>
        </div>
      </div>
    );
  }

  const venueNames = [
    'The Brown Palace', 'Four Seasons Denver', 'Ellie Caulkins Opera House',
    'Denver Botanic Gardens', 'Blanc Denver', 'The Cable Center',
    'Wellshire Event Center', 'Seawell Grand Ballroom', 'Moss Denver',
    'The Manor House', 'Lionsgate Event Center', 'Cielo at Castle Pines',
    'The Wright Room', 'Denver Museum of Nature & Science', 'Baldoria on the Water',
  ];

  return (
    <div className="min-h-screen bg-[#FBFBF9] overflow-hidden" data-testid="vendor-partner-page">
      <Helmet>
        <title>{config.metaTitle}</title>
        <meta name="description" content={config.metaDescription} />
        <link rel="canonical" href={`https://eventism.com/partners/${config.slug}`} />
      </Helmet>

      {/* ==================== HERO ==================== */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 px-6 md:px-12 lg:px-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Copy */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] border border-[#FF4400] px-3 py-1.5 mb-6">
              {config.title} Partner Program
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9] text-[#0A0A0A] mb-6 whitespace-pre-line">
              {config.headline}
            </h1>
            <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed mb-10 max-w-xl">
              {config.subheadline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 bg-[#FF4400] text-white px-8 py-4 font-bold tracking-wide hover:bg-[#E63D00] transition-colors"
                data-testid="hero-signup-button"
              >
                {config.ctaText} <ArrowRight size={20} weight="bold" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 border border-[#0A0A0A] text-[#0A0A0A] px-8 py-4 font-bold tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors"
                data-testid="hero-learn-more"
              >
                See How It Works
              </a>
            </div>
          </motion.div>

          {/* Right: Image Collage */}
          <motion.div className="relative" style={{ y: heroY }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10"
            >
              <img
                src={config.heroImage}
                alt={`${config.singular} at an event`}
                className="w-full h-[400px] md:h-[500px] object-cover"
                loading="eager"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-8 -left-8 w-48 h-48 md:w-64 md:h-64 z-20 border-4 border-[#FBFBF9] shadow-2xl"
            >
              <img
                src={config.heroImage2}
                alt={`${config.singular} event atmosphere`}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </motion.div>
            {/* Accent shape */}
            <div className="absolute top-8 -right-4 w-24 h-24 bg-[#FF4400] -z-10" />
          </motion.div>
        </div>
      </section>

      {/* ==================== VENUE MARQUEE ==================== */}
      <section className="bg-[#F0F0EC] py-5 border-y border-[#E5E5E0]" data-testid="venue-marquee">
        <Marquee speed={30} gradient={false} pauseOnHover>
          {venueNames.map((name, i) => (
            <span key={i} className="mx-6 text-sm font-bold uppercase tracking-[0.15em] text-[#8A8A83]">
              {name} <span className="text-[#D4D4CE] mx-2">&bull;</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ==================== BENEFITS BENTO ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">Why Eventism</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              Everything you need to<br />grow your {config.singular.toLowerCase()} business.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {config.benefitCards.map((card, i) => {
              const IconComponent = ICON_MAP[card.icon] || Star;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="bg-white border border-[#E5E5E0] p-8 hover:-translate-y-1 hover:shadow-xl hover:border-[#D4D4CE] transition-all duration-300"
                  data-testid={`benefit-card-${i}`}
                >
                  <div className="w-12 h-12 bg-[#F0F0EC] flex items-center justify-center mb-5">
                    <IconComponent size={24} weight="duotone" className="text-[#FF4400]" />
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight text-[#0A0A0A] mb-3">{card.title}</h3>
                  <p className="text-[#525252] leading-relaxed">{card.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Mid-section CTA */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-16 text-center">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#FF4400] text-white px-8 py-4 font-bold tracking-wide hover:bg-[#E63D00] transition-colors"
              data-testid="benefits-cta"
            >
              Join as a {config.singular} <ArrowRight size={18} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== PLATFORM STRATEGY / HOW IT WORKS ==================== */}
      <section id="how-it-works" className="bg-[#0A0A0A] text-white py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="strategy-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left: Sticky heading */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="lg:sticky lg:top-32 lg:self-start">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">How It Works</span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] mb-6">
                Our platform<br />works for you.
              </h2>
              <p className="text-[#8A8A83] text-lg leading-relaxed max-w-md">
                Eventism isn't just a listing site. We build a network effect that grows your business every time we add a new venue partner.
              </p>
            </motion.div>

            {/* Right: Steps */}
            <div className="space-y-12">
              {config.strategySteps.map((step, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="relative pl-16 border-l border-[#333]"
                  data-testid={`strategy-step-${i}`}
                >
                  <span className="absolute left-0 -translate-x-1/2 w-8 h-8 bg-[#FF4400] flex items-center justify-center text-xs font-bold text-white">
                    {step.num}
                  </span>
                  <h3 className="text-2xl font-bold tracking-tight mb-3">{step.title}</h3>
                  <p className="text-[#8A8A83] leading-relaxed">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Strategy CTA */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-20 text-center">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 border border-white text-white px-8 py-4 font-bold tracking-wide hover:bg-white hover:text-[#0A0A0A] transition-colors"
              data-testid="strategy-cta"
            >
              Apply Now — It's Free <ArrowRight size={18} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== REVENUE / STATS ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FBFBF9]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">The Numbers</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              Real results for real {config.title.toLowerCase()}.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {config.stats.map((stat, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="text-center p-12 bg-white border border-[#E5E5E0] hover:shadow-xl transition-shadow"
                data-testid={`stat-card-${i}`}
              >
                <p className="text-6xl sm:text-7xl font-black tracking-tighter text-[#0A0A0A] mb-4">
                  {stat.value}
                </p>
                <p className="text-[#525252] text-sm font-medium uppercase tracking-[0.1em]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SAMPLE PREVIEWS ==================== */}
      {config.platformPreview && (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="previews-section">
          <div className="max-w-7xl mx-auto">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">See What You Get</span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
                Here's how you'll look<br />on Eventism.
              </h2>
              <p className="text-[#525252] text-lg mt-4 max-w-xl mx-auto">We build your professional listing and custom marketing pages. Here's a preview of what yours could look like.</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* ===== Platform Service Card ===== */}
              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A8A83] mb-4">Your Service Listing</p>
                <div
                  className="rounded-2xl overflow-hidden shadow-2xl hover:shadow-[0_25px_60px_rgba(0,0,0,0.4)] transition-shadow cursor-pointer"
                  style={{ background: '#13141B' }}
                  onClick={() => setShowPreviewModal(true)}
                  data-testid="sample-listing-preview"
                >
                  {/* Card Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={config.platformPreview.cardImage}
                      alt={config.platformPreview.cardTitle}
                      className="w-full h-full object-cover"
                    />
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex gap-2">
                      {config.platformPreview.cardBadges.map((badge, i) => (
                        <span
                          key={badge}
                          className="text-[11px] font-bold text-white px-3 py-1 rounded-md"
                          style={{ background: i === 0 ? '#F59E0B' : '#10B981' }}
                          data-testid={`card-badge-${badge.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5">
                    <h3 className="text-white font-bold text-xl mb-1.5" data-testid="card-title">
                      {config.platformPreview.cardTitle}
                    </h3>
                    <p className="text-[#9CA3AF] text-sm mb-3">{config.platformPreview.cardDescription}</p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span
                        className="text-xs font-semibold px-3 py-1 rounded-full"
                        style={{ background: '#9DE973', color: '#111827' }}
                        data-testid="card-category-tag"
                      >
                        {config.platformPreview.cardCategory}
                      </span>
                      <span
                        className="text-xs font-semibold text-white px-3 py-1 rounded-full"
                        style={{ background: '#1F2937' }}
                        data-testid="card-subcategory-tag"
                      >
                        {config.platformPreview.cardSubcategory}
                      </span>
                    </div>

                    {/* Price */}
                    <div className="mb-5">
                      <span className="text-2xl font-bold" style={{ color: '#60A5FA' }} data-testid="card-price">
                        {config.platformPreview.cardPrice}
                      </span>
                      <span className="text-[#6B7280] text-sm">{config.platformPreview.cardPriceUnit}</span>
                      <p className="text-[#6B7280] text-xs mt-0.5">{config.platformPreview.cardPriceLabel}</p>
                    </div>

                    {/* CTA Button */}
                    <button
                      className="w-full py-3 rounded-xl text-white font-bold text-sm tracking-wide transition-all hover:brightness-110"
                      style={{ background: '#8B5CF6' }}
                      onClick={(e) => { e.stopPropagation(); setShowPreviewModal(true); }}
                      data-testid="card-customize-btn"
                    >
                      Customize & Add
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* ===== Platform Modal Preview ===== */}
              <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A8A83] mb-4">Service Detail View</p>
                <div
                  className="rounded-2xl overflow-hidden shadow-2xl"
                  style={{ background: '#13141B' }}
                  data-testid="sample-modal-preview"
                >
                  {/* Modal Hero Image */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={config.platformPreview.modalImages[0]}
                      alt={config.platformPreview.cardTitle}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Thumbnail Gallery */}
                  <div className="flex gap-2 p-3 pb-0">
                    {config.platformPreview.modalImages.slice(1, 5).map((img, i) => (
                      <div key={i} className="flex-1 h-14 rounded-lg overflow-hidden border-2 border-transparent" style={i === 0 ? { borderColor: '#8B5CF6' } : {}}>
                        <img src={img} alt={`Preview ${i + 2}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>

                  {/* Modal Body */}
                  <div className="p-5 pt-4">
                    {/* Title + Tag + Price Row */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-white font-bold text-lg leading-tight">{config.platformPreview.cardTitle}</h3>
                        <span
                          className="inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-full mt-1.5"
                          style={{ background: '#9DE973', color: '#111827' }}
                        >
                          {config.platformPreview.cardCategory}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xl font-bold" style={{ color: '#60A5FA' }}>{config.platformPreview.cardPrice}</span>
                        <span className="text-[#6B7280] text-xs">{config.platformPreview.cardPriceUnit}</span>
                        <p className="text-[#6B7280] text-[10px]">{config.platformPreview.cardPriceLabel}</p>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-0 border-b border-[#2A2B35] mb-4">
                      {['Overview', "What's Included", 'Host Requirements', 'Customize'].map((tab, i) => (
                        <div
                          key={tab}
                          className="px-3 py-2.5 text-xs font-medium cursor-default"
                          style={{
                            color: i === 0 ? '#FFFFFF' : '#6B7280',
                            borderBottom: i === 0 ? '2px solid #8B5CF6' : '2px solid transparent',
                          }}
                          data-testid={`modal-tab-${tab.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        >
                          {tab}
                        </div>
                      ))}
                    </div>

                    {/* What to Expect */}
                    <div className="mb-4">
                      <h4 className="text-white font-semibold text-sm mb-2">What to Expect</h4>
                      <p className="text-[#9CA3AF] text-xs leading-relaxed">{config.platformPreview.modalWhatToExpect}</p>
                    </div>

                    {/* Service Type */}
                    <div className="flex items-center justify-between mb-4 p-3 rounded-lg" style={{ background: '#1A1B25' }}>
                      <div>
                        <p className="text-[#6B7280] text-[10px] uppercase tracking-wider mb-0.5">Service Type</p>
                        <p className="text-white text-sm font-semibold">{config.platformPreview.modalServiceType}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold" style={{ color: '#60A5FA' }}>{config.platformPreview.cardPrice}</span>
                        <span className="text-[#6B7280] text-xs">{config.platformPreview.cardPriceUnit}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-default"
                        style={{ background: '#2A2B35', color: '#FFFFFF' }}
                        data-testid="modal-close-btn"
                      >
                        Close
                      </button>
                      <button
                        className="flex-[2] py-2.5 rounded-xl text-white text-sm font-bold cursor-default"
                        style={{ background: '#8B5CF6' }}
                        data-testid="modal-customize-btn"
                      >
                        Customize & Add
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-12 text-center">
              <p className="text-[#525252] mb-4">We build all of this for you — free when you join.</p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-[#FF4400] text-white px-8 py-4 font-bold tracking-wide hover:bg-[#E63D00] transition-colors"
                data-testid="previews-cta"
              >
                Get Your Listing <ArrowRight size={18} weight="bold" />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ==================== PHOTO GALLERY ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F0F0EC]" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">In Action</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              This could be you.
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {config.galleryImages.map((img, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className={`overflow-hidden ${i === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
              >
                <img
                  src={img}
                  alt={`${config.singular} at event ${i + 1}`}
                  className={`w-full object-cover hover:scale-105 transition-transform duration-500 ${i === 0 ? 'h-64 md:h-full' : 'h-48 md:h-64'}`}
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>

          {/* Gallery CTA */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-16 text-center">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#FF4400] text-white px-8 py-4 font-bold tracking-wide hover:bg-[#E63D00] transition-colors"
              data-testid="gallery-cta"
            >
              Join the Platform <ArrowRight size={18} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== WHAT YOU GET CHECKLIST ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="checklist-section">
        <div className="max-w-4xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">What You Get</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              Everything included<br />when you partner with us.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Professional vendor profile page',
              'Professional marketing pages',
              'SEO-optimized listing',
              'Access to 50+ venue partners',
              'Direct client bookings',
              'Review & rating system',
              'Payment processing & invoicing',
              'Real-time booking notifications',
              'Analytics dashboard',
              'Priority support',
              'Zero upfront cost to join',
              'You set your own prices',
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="flex items-center gap-3 p-4 bg-white border border-[#E5E5E0] hover:border-[#FF4400] transition-colors"
              >
                <div className="w-6 h-6 bg-[#FF4400] flex items-center justify-center shrink-0">
                  <Check size={14} weight="bold" className="text-white" />
                </div>
                <span className="font-medium text-[#0A0A0A]">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F0F0EC]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4400] mb-4 block">FAQ</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]">
              Common questions.
            </h2>
          </motion.div>

          <div>
            {config.faqs.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== OTHER CATEGORIES ==================== */}
      <section className="py-16 px-6 md:px-12 lg:px-24 border-t border-[#E5E5E0]" data-testid="other-categories">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A8A83] mb-6">Also Recruiting</p>
          <div className="flex flex-wrap gap-3">
            {ALL_VENDOR_CATEGORIES.filter(c => c.slug !== config.slug).map(cat => (
              <Link
                key={cat.slug}
                to={`/partners/${cat.slug}`}
                className="text-sm font-bold border border-[#E5E5E0] px-4 py-2 hover:border-[#FF4400] hover:text-[#FF4400] transition-colors"
              >
                {cat.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="bg-[#FF4400] py-24 md:py-32 px-6 md:px-12 lg:px-24 text-center" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9] text-white mb-6">
              Ready to grow your<br />{config.singular.toLowerCase()} business?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-lg mx-auto">
              {config.ctaSubtext}
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-white text-[#FF4400] px-10 py-5 text-lg font-black tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors"
              data-testid="final-signup-button"
            >
              Sign Up Now — It's Free <ArrowRight size={22} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="bg-[#0A0A0A] text-white py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <Link to="/" className="text-2xl font-black tracking-tighter">EVENTISM</Link>
            <p className="text-[#8A8A83] text-sm mt-1">Denver's Event Services Platform</p>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#8A8A83]">
            <Link to="/partners" className="hover:text-white transition-colors">All Partners</Link>
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>

      {/* ==================== FULL-SCREEN PREVIEW MODAL ==================== */}
      {showPreviewModal && config.platformPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
          onClick={() => setShowPreviewModal(false)}
          data-testid="preview-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl"
            style={{ background: '#13141B' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="preview-modal-content"
          >
            {/* Close Button */}
            <button
              onClick={() => setShowPreviewModal(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full text-white/70 hover:text-white transition-colors"
              style={{ background: 'rgba(0,0,0,0.5)' }}
              data-testid="preview-modal-close-x"
            >
              <X size={18} weight="bold" />
            </button>

            {/* Hero Image */}
            <div className="relative h-64 sm:h-72 overflow-hidden">
              <img
                src={config.platformPreview.modalImages[0]}
                alt={config.platformPreview.cardTitle}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Thumbnail Gallery */}
            <div className="flex gap-2 px-5 pt-4">
              {config.platformPreview.modalImages.slice(1, 5).map((img, i) => (
                <div
                  key={i}
                  className="flex-1 h-16 rounded-lg overflow-hidden border-2"
                  style={{ borderColor: i === 0 ? '#8B5CF6' : 'transparent' }}
                >
                  <img src={img} alt={`Preview ${i + 2}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>

            {/* Modal Body */}
            <div className="p-5 pt-4">
              {/* Title Row */}
              <div className="flex items-start justify-between mb-1">
                <h3 className="text-white font-bold text-2xl">{config.platformPreview.cardTitle}</h3>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="text-xs font-semibold px-3 py-1 rounded-full"
                  style={{ background: '#9DE973', color: '#111827' }}
                >
                  {config.platformPreview.cardCategory}
                </span>
                <span className="text-[#9CA3AF] text-sm">{config.platformPreview.cardDescription}</span>
              </div>

              {/* Price */}
              <div className="mb-4">
                <span className="text-3xl font-bold" style={{ color: '#60A5FA' }}>{config.platformPreview.cardPrice}</span>
                <span className="text-[#6B7280] text-sm">{config.platformPreview.cardPriceUnit}</span>
                <p className="text-[#6B7280] text-xs">{config.platformPreview.cardPriceLabel}</p>
              </div>

              {/* Tabs */}
              <div className="flex gap-0 border-b border-[#2A2B35] mb-5">
                {['Overview', "What's Included", 'Host Requirements', 'Customize'].map((tab, i) => (
                  <div
                    key={tab}
                    className="px-4 py-3 text-sm font-medium cursor-default"
                    style={{
                      color: i === 0 ? '#FFFFFF' : '#6B7280',
                      borderBottom: i === 0 ? '2px solid #8B5CF6' : '2px solid transparent',
                    }}
                  >
                    {tab}
                  </div>
                ))}
              </div>

              {/* What to Expect */}
              <div className="mb-5">
                <h4 className="text-white font-semibold text-lg mb-3">What to Expect</h4>
                <p className="text-[#9CA3AF] text-sm leading-relaxed">{config.platformPreview.modalWhatToExpect}</p>
              </div>

              {/* Service Type */}
              <div className="flex items-center justify-between p-4 rounded-xl mb-6" style={{ background: '#1A1B25' }}>
                <div>
                  <p className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Service Type</p>
                  <p className="text-white font-semibold">{config.platformPreview.modalServiceType}</p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold" style={{ color: '#60A5FA' }}>{config.platformPreview.cardPrice}</span>
                  <span className="text-[#6B7280] text-sm">{config.platformPreview.cardPriceUnit}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold transition-colors hover:brightness-125"
                  style={{ background: '#2A2B35', color: '#FFFFFF' }}
                  data-testid="preview-modal-close-btn"
                >
                  Close
                </button>
                <button
                  className="flex-[2] py-3 rounded-xl text-white text-sm font-bold cursor-default"
                  style={{ background: '#8B5CF6' }}
                  data-testid="preview-modal-customize-btn"
                >
                  Customize & Add
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
